<script lang="ts" setup>
  import {
    DocumentTemplatesService,
    WordDocumentTemplatesService,
    DocumentsService,
    type TDocumentTemplate,
    type TWordDocumentTemplate,
    type TDocument,
    ImagePresetEnum,
    type ImageproxyImagePreset
  } from '@/types/generated'
  import { computed, onMounted, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import { toast } from '@/components/ui/toast/use-toast'
  import { ROUTES } from '@/router/routes'
  import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts'
  import { Eye, Copy, X } from 'lucide-vue-next'

  const props = defineProps({
    templateUuid: {
      type: String,
      required: true
    },
    documentUuid: {
      type: String,
      required: false
    }
  })

  const document = ref<TDocument>(null)
  const template = ref<TDocumentTemplate | TWordDocumentTemplate>(null)
  const loading = ref(true)

  const router = useRouter()

  onMounted(async () => {
    try {
      loading.value = true

      try {
        template.value = await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
          uuid: props.templateUuid
        })
      } catch (htmlError) {
        if (htmlError.status === 404) {
          template.value = await DocumentTemplatesService.documentTemplatesRetrieve({
            uuid: props.templateUuid
          })
        } else {
          throw htmlError
        }
      }

      if (props.documentUuid) {
        document.value = await DocumentsService.documentsRetrieve({
          uuid: props.documentUuid
        })
      }
    } catch (error) {
      console.error('Error fetching document:', error)
    } finally {
      loading.value = false
    }
  })

  const documentPreview = computed<ImageproxyImagePreset | null>(() => {
    if (!document.value) return null
    return document.value.images.find((image) => image.image_preset === ImagePresetEnum.LARGE)
  })

  const templatePreview = computed<ImageproxyImagePreset | null>(() => {
    if (!template.value) return null
    return template.value.document.images.find(
      (image) => image.image_preset === ImagePresetEnum.LARGE
    )
  })

  const isWordTemplate = computed(() => {
    return template.value && 'docx_template' in template.value
  })

  const viewTemplate = () => {
    if (!template.value) return
    const path = ROUTES.ADMIN.WORD_TEMPLATES.REVIEW_DETAIL.replace(':uuid', template.value.uuid)
    router.push(path)
  }

  const copyTemplateName = async () => {
    if (!template.value) return
    try {
      await navigator.clipboard.writeText(template.value.name)
      toast({
        title: 'Success',
        description: 'Template name copied to clipboard'
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy template name',
        variant: 'destructive'
      })
    }
  }

  // Setup keyboard shortcuts
  const { isHelpOpen, shortcuts } = useKeyboardShortcuts([
    {
      key: 'v',
      action: viewTemplate,
      description: 'View template'
    },
    {
      key: 'c',
      action: copyTemplateName,
      description: 'Copy template name'
    }
  ])
</script>
<template>
  <div>
    <div
      v-if="loading"
      class="flex items-center justify-center py-8"
    >
      <div class="loader"></div>
    </div>
    <div
      v-else
      class="document-comparison-container"
    >
      <!-- Template Name Header -->
      <div
        class="mb-6 rounded-lg border border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 p-5 shadow-sm"
      >
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <p class="mb-1 text-sm font-medium text-gray-600">Document Template</p>
            <h2 class="text-2xl font-bold text-gray-900">
              <span v-if="template">{{ template.name }}</span>
              <span
                v-else
                class="italic text-gray-400"
              >
                Template information unavailable
              </span>
            </h2>
            <!-- Template Badges -->
            <div
              v-if="template"
              class="mt-2 flex items-center space-x-2"
            >
              <span class="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800">
                {{ isWordTemplate ? 'Word' : 'HTML' }}
              </span>
              <span
                v-if="isWordTemplate && template.status"
                class="rounded-full px-3 py-1 text-xs font-semibold"
                :class="{
                  'bg-yellow-100 text-yellow-800': template.status === 'draft',
                  'bg-orange-100 text-orange-800': template.status === 'in_review',
                  'bg-green-100 text-green-800': template.status === 'approved'
                }"
              >
                {{
                  template.status.replace('_', ' ').charAt(0).toUpperCase() +
                  template.status.slice(1).replace('_', ' ')
                }}
              </span>
              <span
                v-if="template.is_jupus_template"
                class="rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-800"
              >
                Jupus
              </span>
            </div>
          </div>
          <div
            v-if="template"
            class="flex items-center space-x-2"
          >
            <button
              class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium
                text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
                focus:ring-offset-2"
              title="View this template (V)"
              @click="viewTemplate"
            >
              <Eye class="mr-1.5 h-4 w-4" />
              View
            </button>

            <button
              class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium
                text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
                focus:ring-offset-2"
              title="Copy template name to clipboard (C)"
              @click="copyTemplateName"
            >
              <Copy class="mr-1.5 h-4 w-4" />
              Copy Name
            </button>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <!-- Template Document -->
        <div class="document-panel rounded border p-4 shadow-sm">
          <h3 class="mb-4 text-lg font-semibold">Template</h3>
          <div
            v-if="template"
            class="document-content"
          >
            <img
              :src="templatePreview?.url"
              alt="Template Preview"
              class="mb-2"
            />
          </div>
        </div>

        <!-- Generated Document -->
        <div class="document-panel rounded border p-4 shadow-sm">
          <h3 class="mb-4 text-lg font-semibold">Generated Document</h3>
          <div
            v-if="document"
            class="document-content"
          >
            <img
              :src="documentPreview?.url"
              alt="Document Preview"
              class="mb-2"
            />
          </div>
          <div
            v-else
            class="text-sm text-gray-500"
          >
            No generated document information available
          </div>
        </div>
      </div>
    </div>

    <!-- Keyboard Shortcuts Help Overlay -->
    <div
      v-if="isHelpOpen"
      class="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50"
      @click="isHelpOpen = false"
    >
      <div
        class="max-w-md rounded-lg bg-white p-6 shadow-xl"
        @click.stop
      >
        <div class="mb-4 flex items-center justify-between">
          <h3 class="text-lg font-semibold">Keyboard Shortcuts</h3>
          <button
            class="text-gray-400 hover:text-gray-600"
            title="Close (Esc)"
            @click="isHelpOpen = false"
          >
            <X class="h-5 w-5" />
          </button>
        </div>

        <div class="space-y-3">
          <div class="text-sm">
            <div class="mb-2 font-medium text-gray-700">Actions</div>
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <span class="text-gray-600">View template</span>
                <kbd
                  class="rounded border border-gray-200 bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800"
                >
                  V
                </kbd>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-gray-600">Copy template name</span>
                <kbd
                  class="rounded border border-gray-200 bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800"
                >
                  C
                </kbd>
              </div>
            </div>
          </div>

          <div class="text-sm">
            <div class="mb-2 font-medium text-gray-700">General</div>
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <span class="text-gray-600">Show this help</span>
                <kbd
                  class="rounded border border-gray-200 bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800"
                >
                  ?
                </kbd>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-gray-600">Close dialogs</span>
                <kbd
                  class="rounded border border-gray-200 bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800"
                >
                  Esc
                </kbd>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-4 border-t border-gray-200 pt-4">
          <p class="text-xs text-gray-500">Press any key to perform its action</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .document-comparison-container {
    max-width: 100%;
    overflow-x: hidden;
  }

  .document-panel {
    max-height: 80vh;
    overflow-y: auto;
    background-color: white;
  }

  .document-content {
    min-height: 300px;
  }

  .loader {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
